import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import {
  Form,
  Button,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Row,
  Column,
  Tag,
  InlineNotification,
} from '@carbon/react';
import { Add, Edit, TrashCan } from '@carbon/react/icons';

import { Page } from '../../components/Page';
import {
  Panel,
  PanelHeader,
  PanelLabel,
  PanelFooter,
} from '../../components/Panel';

import {
  useGetDefaultStreamsQuery,
  useCreateDefaultStreamMutation,
  useDeleteDefaultStreamMutation,
} from '@/redux/new_services/defaultStreams';
import { useGetGroupsQuery } from '@/redux/new_services/groups';
import { useConfirmRemoveSomething } from '@/lib/modal';
import { showToast } from '@/redux/new_services/toasts';
import { hideForm } from '@/redux/new_services/forms';

import './_settings.scss';

// Default-Streams admin page: one row per known group plus a row for the
// "Allgemein" fallback (group_id = NULL). For each row the admin either
// has an existing default stream (with "Bearbeiten" + "Entfernen" actions)
// or a "Anlegen" button that creates an empty default and opens it in the
// regular stream editor for content management.
export const DefaultStreams = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: defaultStreams = [], isLoading } = useGetDefaultStreamsQuery();
  const { data: groupsResp, isLoading: isLoadingGroups } = useGetGroupsQuery();
  const groups = useMemo(() => groupsResp?.data || [], [groupsResp]);

  const [createDefaultStream, { isLoading: isCreating }] = useCreateDefaultStreamMutation();
  const [deleteDefaultStream, { isLoading: isDeleting }] = useDeleteDefaultStreamMutation();

  const confirmRemoveDefault = useConfirmRemoveSomething(
    'Default-Stream entfernen',
    'Möchten Sie den Default-Stream wirklich entfernen? Geräte, die ihn aktuell beziehen, fallen anschließend auf den Allgemein-Default zurück (oder bekommen gar nichts, wenn keiner gesetzt ist).'
  );

  // Index existing defaults by group_id for quick lookup. NULL group_id
  // is the "Allgemein" fallback — we treat it as key "general".
  const byGroup = useMemo(() => {
    const map = new Map();
    for (const s of defaultStreams) {
      const key = s.group_id ? `g_${s.group_id}` : 'general';
      map.set(key, s);
    }
    return map;
  }, [defaultStreams]);

  // Rows the table renders: Allgemein on top, then one per known group.
  const rows = useMemo(() => {
    const result = [{ key: 'general', label: 'Allgemein (Fallback)', groupId: null }];
    for (const g of groups) {
      result.push({ key: `g_${g.model.ID}`, label: g.name, groupId: g.model.ID });
    }
    return result;
  }, [groups]);

  const [error, setError] = useState(null);

  const handleCreate = async (groupId) => {
    setError(null);
    try {
      const res = await createDefaultStream({ group_id: groupId }).unwrap();
      dispatch(showToast({
        kind: 'success',
        title: 'Default-Stream angelegt',
        caption: 'Du kannst ihn jetzt mit Inhalten füllen.',
      }));
      const newId = res?.data?.model?.ID;
      if (newId) {
        dispatch(hideForm());
        navigate(`/stream/${newId}`);
      }
    } catch (e) {
      const msg = e?.data?.message || e?.data || 'Default-Stream konnte nicht angelegt werden.';
      setError(typeof msg === 'string' ? msg : 'Default-Stream konnte nicht angelegt werden.');
      dispatch(showToast({
        kind: 'error',
        title: 'Anlegen fehlgeschlagen',
        caption: typeof msg === 'string' ? msg : '',
      }));
    }
  };

  const handleEdit = (streamId) => {
    if (!streamId) return;
    navigate(`/stream/${streamId}`);
  };

  const handleDelete = (stream) => {
    if (!stream?.model?.ID) return;
    confirmRemoveDefault(() => {
      deleteDefaultStream(stream.model.ID)
        .unwrap()
        .then(() => {
          dispatch(showToast({
            kind: 'success',
            title: 'Default-Stream entfernt',
            caption: '',
          }));
        })
        .catch((err) => {
          dispatch(showToast({
            kind: 'error',
            title: 'Löschen fehlgeschlagen',
            caption: err?.data?.message || 'Default-Stream konnte nicht entfernt werden.',
          }));
        });
    });
  };

  return (
    <Page
      pageTitle="Default-Streams"
      hasSideNav
      isLoading={isLoading || isLoadingGroups || isCreating || isDeleting}
    >
      <Row>
        <Column md={5}>
          <Row className="small_row">
            <Column md={4}>
              <Panel small>
                <PanelHeader header>Info</PanelHeader>
                <PanelLabel>
                  Default-Streams sind die Fallback-Inhalte, die ein
                  Micast-System abspielt, wenn dem Gerät noch kein Stream
                  zugewiesen wurde. So muss ein frisch eingerichtetes
                  System nicht erst auf einen Operator warten — es spielt
                  sofort etwas Sinnvolles.
                </PanelLabel>
                <PanelFooter />
              </Panel>
            </Column>
            <Column md={4}>
              <Panel small>
                <PanelHeader header>So wird ausgewählt</PanelHeader>
                <PanelLabel>
                  Das System sucht zuerst einen Default-Stream, dessen
                  Gruppe zur Organisation des Geräts gehört. Findet es
                  keinen, greift der „Allgemein"-Default. Default-Streams
                  erscheinen nicht in der normalen Stream-Übersicht.
                </PanelLabel>
                <PanelFooter />
              </Panel>
            </Column>
          </Row>
        </Column>
      </Row>

      {error && (
        <Row>
          <Column md={8}>
            <InlineNotification
              kind="error"
              title="Fehler"
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
              lowContrast
            />
          </Column>
        </Row>
      )}

      <Row>
        <Column md={8}>
          <Form>
            <Table size="sm">
              <TableHead>
                <TableRow>
                  <TableHeader>Gruppe</TableHeader>
                  <TableHeader>Default-Stream</TableHeader>
                  <TableHeader />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const existing = byGroup.get(row.key);
                  return (
                    <TableRow key={row.key}>
                      <TableCell>
                        {row.key === 'general' ? (
                          <>
                            <strong>{row.label}</strong>{' '}
                            <Tag type="cool-gray" size="sm">Fallback</Tag>
                          </>
                        ) : (
                          row.label
                        )}
                      </TableCell>
                      <TableCell>
                        {existing ? existing.name : <em>— kein Default gesetzt —</em>}
                      </TableCell>
                      <TableCell>
                        {existing ? (
                          <>
                            <Button
                              renderIcon={(props) => <Edit size={16} {...props} />}
                              iconDescription="Bearbeiten"
                              hasIconOnly
                              size="sm"
                              kind="primary"
                              onClick={() => handleEdit(existing.model.ID)}
                              className="row_editing_button"
                            />
                            <Button
                              renderIcon={(props) => <TrashCan size={16} {...props} />}
                              iconDescription="Entfernen"
                              hasIconOnly
                              size="sm"
                              kind="danger"
                              onClick={() => handleDelete(existing)}
                              className="row_editing_button"
                            />
                          </>
                        ) : (
                          <Button
                            renderIcon={(props) => <Add size={16} {...props} />}
                            size="sm"
                            kind="tertiary"
                            onClick={() => handleCreate(row.groupId)}
                          >
                            Anlegen
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Form>
        </Column>
      </Row>
    </Page>
  );
};

export default DefaultStreams;
